import React from "react";
import { useInView } from "react-intersection-observer";
import { cn } from "../../../lib/helpers";
import { ShowEmoji } from "../../emoji";
import { FeatureCompareCell } from "./pricing-comparison";

// The title cell always spans 2 columns (col-span-2 in FeatureCompareCell), so the grid
// needs plans.length + 2 columns total, not just one per plan.
const GRID_COLS_CLASS = {
  0: "grid-cols-2",
  1: "grid-cols-3",
  2: "grid-cols-4",
  3: "grid-cols-5",
  4: "grid-cols-6",
  5: "grid-cols-7",
  6: "grid-cols-8",
};

export const PricingComparisonSection = ({ section }) => {
  const plans = (section.plans || []).map((p) => ({
    ...p,
    planKey: p.planKey?.current || p.planKey,
  }));
  const comparison = section.comparison || [];

  // Plans is optional — a comparison-only block (no Plans list authored) still needs a
  // column per value, so fall back to however many values the widest row actually has.
  const columnCount = Math.max(plans.length, ...comparison.map((row) => (row.values || []).length), 0);
  const columns = Array.from({ length: columnCount }, (_, i) => plans[i] || { planKey: `col-${i}` });

  return (
    <div className="text-lg">
      {comparison.map((row) => (
        <FeatureCompareRow row={row} plans={columns} values={row.values || []} key={row._key} />
      ))}
    </div>
  );
};

const FeatureCompareRow = ({ row, plans, values }) => {
  const isHeaderRow = !!row.isHeader;
  const { ref: sentinelRef, inView } = useInView({ rootMargin: "-57px 0px 0px 0px" });
  const gridClass = GRID_COLS_CLASS[plans.length] || "grid-cols-8";

  return (
    <>
      {isHeaderRow && <div ref={sentinelRef} className="h-px" />}
      <div
        className={cn(
          "grid",
          gridClass,
          isHeaderRow
            ? cn("sticky z-10 mt-8 overflow-hidden", inView && "sm:rounded-t-xl")
            : "bg-white odd:bg-light-40 hover:bg-dark-2"
        )}
        style={isHeaderRow ? { top: "3.5rem" } : null}
      >
        <FeatureCompareCell
          {...{ isHeaderRow }}
          isTitleCell={true}
          message={
            <span className="flex items-center">
              <ShowEmoji name={row.emoji} className="w-8 h-8 mr-2" /> {row.title}
            </span>
          }
          summary={row.summary}
          tooltip={row.summaryTooltip}
          _key={`${row._key}-summary`}
        />
        {plans.map((plan, i) => {
          const value = values[i];
          return (
            <FeatureCompareCell
              {...{ isHeaderRow }}
              message={isHeaderRow ? value?.message || plan.title : value?.message}
              tooltip={value?.tooltip || null}
              isChecked={value?.included}
              checkColor="dark-40"
              _key={`${row._key}-${plan.planKey}`}
              path={value?.path}
              key={plan.planKey}
            />
          );
        })}
      </div>
    </>
  );
};
